import { IS_CURRENT_TENANT_KSA } from "configs/agency"
import { DASHBOARD_PATH, HOME_PATH, LOGIN_PATH } from "constants/app-paths"
import AppLayout from "layout"
import { Suspense, useTransition, useEffect } from "react"
import { useSelector } from "react-redux"
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router"
import { PROTECTED_ROUTES, PUBLIC_ROUTES } from "routes/constants"

/**
 * Wraps a route component with Suspense and scroll restoration
 * @param {Object} props
 * @param {React.Component} props.component - The component to render
 * @param {boolean} props.isLoggedIn - Whether the user is logged in
 * @param {React.ReactNode} [props.fallback] - Optional custom fallback component
 * @param {Object} props.rest - Additional props to pass to the component
 */
const RouteWrapper = ({ component: Component, isLoggedIn, fallback = null, ...props }) => {
  const location = useLocation()

  // Improved scroll restoration on route change
  useEffect(() => {
    // Prevent browser's automatic scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    
    // Smooth scroll to top with a small delay to allow content to render
    const timer = setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    }, 50)

    return () => clearTimeout(timer)
  }, [location.pathname])

  return (
    <Suspense fallback={fallback || <div></div>}>
      <Component isLoggedIn={isLoggedIn} {...props} />
    </Suspense>
  )
}

/**
 * Handles authentication redirection
 */
const RequireAuth = ({ location }) => {
  const isLoggedIn = useSelector(state => state.app.auth?.isLoggedIn)
  return isLoggedIn ? <Outlet /> : <Navigate to="/" state={{ from: location }} replace />
}

/**
 * Handles public page redirections
 */
const PublicPagesRedirection = () => {
  const isLoggedIn = useSelector(state => state.app.auth?.isLoggedIn)
  const { pathname } = useLocation()

  if (isLoggedIn && pathname === "/") {
    return <Navigate to={DASHBOARD_PATH} replace />
  }
  return <Outlet />
}

/**
 * Main routing component
 */
const AppRoutes = () => {
  const isLoggedIn = useSelector(state => state.app.auth?.isLoggedIn)
  const location = useLocation()
  const { pathname } = location

  // Handle redirections for logged-in users
  const handleRedirections = () => {
    if (IS_CURRENT_TENANT_KSA) return null
    
    if (!isLoggedIn) return null

    return (
      <>
        {pathname === HOME_PATH && <Navigate to={DASHBOARD_PATH} replace />}
        {pathname === LOGIN_PATH && <Navigate to={DASHBOARD_PATH} replace />}
      </>
    )
  }

  // Handle nested routes
  const renderNestedRoutes = ({ childRoutes }) => {
    if (!childRoutes) return null

    return childRoutes.map(childRoute => (
      <Route
        key={childRoute.path}
        path={childRoute.path}
        element={
          childRoute?.navigateTo ? (
            <Navigate to={childRoute.navigateTo} replace />
          ) : (
            <RouteWrapper 
              component={childRoute?.Component} 
              isLoggedIn={isLoggedIn} 
              fallback={childRoute?.fallback}
            />
          )
        }
      />
    ))
  }

  // Render route with its wrapper
  const renderRoute = route => (
    <Route
      key={route.path}
      path={route.path}
      element={
        <RouteWrapper 
          component={route?.Component} 
          isLoggedIn={isLoggedIn} 
          fallback={route?.fallback}
        />
      }
    >
      {renderNestedRoutes({ childRoutes: route?.children })}
    </Route>
  )

  return (
    <Routes>
      <Route
        path={HOME_PATH}
        element={
          <AppLayout>
            <Suspense fallback={null}>
              {handleRedirections()}
              <Outlet />
            </Suspense>
          </AppLayout>
        }
      >
        {/* Public Routes */}
        {PUBLIC_ROUTES.map(renderRoute)}

        {/* Protected Routes */}
        <Route element={<RequireAuth location={location} />}>{PROTECTED_ROUTES?.map(renderRoute)}</Route>

        {/* Fallback Route */}
        <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
      </Route>
    </Routes>
  )
}

export default AppRoutes
